import { Link } from 'react-router-dom'
import { CONSENT_REOPEN_EVENT, getMeasurementId } from '@/lib/analytics/consent'
import type { StorefrontIdentity } from '@/features/clubs/storefront'
import styles from './AppFooter.module.css'
import storefront from './StorefrontFooter.module.css'

function phoneHref(value: string): string {
  return value.replace(/[^\d+]/g, '')
}

/**
 * THE CLUB'S OWN FOOTER (Club Storefront, brick 1) — how to reach THIS club, plus the
 * shortest legal tail that is still honest.
 *
 * It replaces the site footer's three columns of platform links (`/pricing`, `/clubs`,
 * `/tournaments`, the wordmark, `/for-clubs`) with what the club already told us about
 * itself: address, email, phone, website. Nothing here is new data to fill in — every
 * field is one the club edits in its own setup.
 *
 * "POWERED BY THERACKETCENTER.COM" (owner, 2026-09-07) — the one mention of the platform
 * on a club's page, and the page where it most belongs: this really is a club's own
 * branded page, running on our software, and a footer credit is the honest, ordinary way
 * to say so. It is deliberately quiet and deliberately LOW: it sits under the club's own
 * copyright, in the dim mono of the tail rather than the wordmark's type, so the club's
 * name is still the last name a visitor reads. The header stays clean — the platform is
 * not named up there, and storefrontNav.test.mjs holds that line — because a credit at
 * the bottom of a page is a different thing from branding at the top of one.
 *
 * THE LEGAL TAIL STAYS. Privacy and Terms govern the account the visitor may be signing
 * into and the data this software processes on the club's behalf, and Cookie settings is
 * the consent control the banner requires. Those are obligations, not platform
 * advertising, and dropping them to make the page feel more like the club's own would be
 * trading a legal duty for a decoration.
 *
 * BUT OUR LEGAL ENTITY DOES NOT BELONG HERE — asked and answered 2026-09-09, when the
 * copyright and the support address went back onto the player surfaces and the same
 * question was put to this footer. Three reasons it gets the opposite answer:
 *
 *   A COPYRIGHT LINE CLAIMS THE PAGE. What is on this page is the club's — its name, its
 *   address, its courts, its programmes, its photographs. Printing "© Escuela de tenis ITS
 *   S.R.L." underneath would assert ownership of somebody else's content, and printing it
 *   BESIDE the club's would put two copyright holders on one page, which does not read as
 *   thoroughness; it reads as a dispute.
 *
 *   THE VISITOR'S COUNTERPARTY IS THE CLUB. They book the club's court and pay the club,
 *   and the club's name is what reaches their card statement. Naming our S.R.L. in the
 *   same bar implies we are a party to that, which is precisely the confusion a company
 *   identification is supposed to prevent.
 *
 *   WHAT WE OWE IS ALREADY DISCHARGED, ONE CLICK AWAY. "powered by TheRacketCenter.com"
 *   says whose software this is, and Privacy and Terms — right beside it — name the entity
 *   in full and give the address to reach it. That is identification with a hierarchy: the
 *   club is the business the visitor is dealing with, we are the processor behind it, and
 *   the page says so in that order.
 *
 * The rule this leaves is a clean one: a footer names the company whose page it is. Ours
 * on ours, the club's on the club's, and the platform credit as the bridge between them.
 *
 * It reuses AppFooter's CSS module so the club's footer sits on the same hairline, the
 * same column grid and the same bottom bar as every other page — a different set of
 * things in a footer the site already has.
 */
export function StorefrontFooter({ club }: { club: StorefrontIdentity }) {
  const analyticsConfigured = Boolean(getMeasurementId())
  const location = [club.address, [club.city, club.country].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ')
  const hasContact = Boolean(club.contactEmail || club.contactPhone || club.website)

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.columns}>
          <div className={styles.column}>
            <span className={styles.columnHead}>{club.name}</span>
            {location ? <span className={storefront.line}>{location}</span> : null}
          </div>

          {hasContact ? (
            <div className={styles.column}>
              <span className={styles.columnHead}>Contact</span>
              {club.contactEmail ? (
                <a className={styles.columnLink} href={`mailto:${club.contactEmail}`}>
                  {club.contactEmail}
                </a>
              ) : null}
              {club.contactPhone ? (
                <a className={styles.columnLink} href={`tel:${phoneHref(club.contactPhone)}`}>
                  {club.contactPhone}
                </a>
              ) : null}
              {club.website ? (
                <a
                  className={styles.columnLink}
                  href={club.website}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Visit website
                </a>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={styles.bottomBar}>
          <div className={styles.brand}>
            <span className={styles.copy}>
              © {new Date().getFullYear()} {club.name}
            </span>
            <span className={styles.poweredBy}>
              powered by <Link to="/">TheRacketCenter.com</Link>
            </span>
          </div>
          <div className={styles.bottomLinks}>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            {analyticsConfigured ? (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT))}
              >
                Cookie settings
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  )
}
